import type { ExecSessionStatus } from "@k8s-dashboard/contracts";
import { useT } from "@/i18n/I18nProvider";
import { Notice } from "@/features/drill/sessionPanel";

/**
 * Debug Terminal 상태 표시 (ADR 0022 · Issue #10, 다국어는 ADR 0025)
 * --------------------------------------------------------------------------
 * 여기서 만드는 문장은 브라우저만 아는 사실(진행 단계, 남은 시간, 유휴 임박)뿐입니다.
 * 종료 사유와 오류는 서버가 준 문장을 **그대로** 씁니다 — 화면이 사유를 지어내면
 * 사용자가 읽는 문구가 서버가 아는 사실과 달라집니다.
 */

export type ExecPhase = "idle" | "connecting" | "open" | "closed" | "error";

/** 단계 → 메시지 ID. 템플릿으로 만들지 않아야 카탈로그 게이트가 정적으로 셉니다. */
const PHASE_MESSAGE_ID: Record<ExecPhase, string> = {
  idle: "terminal.phase.idle",
  connecting: "terminal.phase.connecting",
  open: "terminal.phase.open",
  closed: "terminal.phase.closed",
  error: "terminal.phase.error",
};

/**
 * 상태 한 문장. 터미널의 텍스트 대체가 같은 문장을 재사용합니다.
 *
 * **남은 시간은 상한이 있을 때만 붙습니다**(ADR 0036). 세션을 시각으로 끝내는 것은 인증
 * 만료 하나이고, 인증이 없는 배포에는 그 시각이 없습니다 — `remainingSeconds`가 0인
 * 이유가 "곧 끝남"이 아니라 "끝나는 시각이 없음"이므로, 세는 척하면 화면이 오지 않을
 * 종료를 카운트다운합니다. 활동과 무관한 종료를 알리는 일은 유휴 경고가 맡습니다.
 */
export function useExecStatusText(phase: ExecPhase, remainingSeconds: number): string {
  const { t } = useT("terminal");
  const base = t(PHASE_MESSAGE_ID[phase]);
  if (phase !== "open" || remainingSeconds <= 0) return base;
  return `${base} · ${t("terminal.remaining", { seconds: remainingSeconds })}`;
}

export function ExecStatus({
  text,
  live,
  idleWarning,
  error,
  ended,
}: {
  /** `useExecStatusText`가 만든 문장. 두 곳이 각자 만들면 서로 다른 말을 합니다. */
  text: string;
  live: boolean;
  idleWarning: boolean;
  error: string;
  ended?: ExecSessionStatus;
}) {
  const { t } = useT("terminal");
  return (
    <>
      <p className="muted" role="status" data-testid="exec-status">
        {text}
      </p>
      {idleWarning && live ? (
        <Notice tone="warning" role="alert" testId="exec-idle-warning">
          {t("terminal.idleWarning")}
        </Notice>
      ) : null}
      {error ? (
        <Notice tone="warning" role="alert" testId="exec-error">
          {error}
        </Notice>
      ) : null}
      {ended ? (
        <Notice role="status" testId="exec-ended">
          {ended.message}
          {typeof ended.exitCode === "number" ? ` (${t("terminal.exitCode", { code: ended.exitCode })})` : ""}
        </Notice>
      ) : null}
    </>
  );
}
